import b from 'b_';
import { CheckCircle, Lock } from 'education-icons';
import { Image, SimpleCallback, Text, useIntl, useMe } from 'platform-components';
import { UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/constants';
import { useMetrika } from 'platform-components/src/utils/hooks/useMetrika';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { CertificateCardButtonChangeData } from 'common.components/certificate-card/__button/_change-data/certificate-card__button_change-data';
import { getAllWebinarSlugsFromConfig } from 'common.components/certificate-card/selectors';
import { printIframe } from 'utils/print-iframe';
import { useCertRenderer } from './useCertRenderer';

import CertificateCardButtonLocked from './__button/_status/certificate-card__button_status_locked';
import CertificateCardButtonOpen from './__button/_status/certificate-card__button_status_open';
import CertificateCardModal from './__modal/certificate-card__modal';

import './certificate-card.scss';

export enum CERTIFICATE_STATUSES {
    OPEN = 'open',
    LOCKED = 'locked',
}

/* eslint-disable camelcase */
export enum CERTIFICATE_VIEWS {
    DEFAULT = 'default',
    WITH_VERIFICATION = 'with_verification',
}

interface Props {
    onPrintButtonClick?: SimpleCallback;
    onSecondaryButtonClick?: SimpleCallback;
    slug: string;
    status: CERTIFICATE_STATUSES;
    view: CERTIFICATE_VIEWS;
}

export const CertificateCard = (props: Props) => {
    const { onPrintButtonClick, onSecondaryButtonClick, slug, status, view } = props;
    const intl = useIntl();
    const me = useMe();
    const { sendGoal } = useMetrika();

    const [modalOpened, setModalOpened] = useState(false);

    const webinarsSlugs = useSelector(getAllWebinarSlugsFromConfig);

    const getCertHTML = useCertRenderer(slug);

    // eslint-disable-next-line complexity
    const handleOpenButtonClick = () => {
        sendGoal('teacher_navigation_lk_download_cert', {
            teacher_id: me?.id,
            cert_id: slug,
            can_open: !!onPrintButtonClick,
        });

        if (onPrintButtonClick) {
            onPrintButtonClick();
        } else if (getCertHTML) {
            printIframe('portfolioCert', getCertHTML());
        }
    };

    const handleLockedButtonClick = () => {
        sendGoal('teacher_navigation_lk_unsolved_cert', { teacher_id: me?.id, cert_id: slug });

        setModalOpened(true);
    };

    const getDefaultImageSrc = () => {
        if (webinarsSlugs.includes(slug)) {
            return intl.formatMessage({ id: 'teacherLK.card.preview.type.webinar' });
        }
        return intl.formatMessage({ id: 'teacherLK.card.preview.type.event' });
    };

    return (
        <>
            <div className="certificate-card">
                <div className="certificate-card__preview-wrapper">
                    <Image
                        alt=""
                        className="certificate-card__preview"
                        src={intl.formatMessage({
                            id: `teacherLK.card.preview.${slug}`,
                            defaultMessage: getDefaultImageSrc(),
                        })}
                    />
                </div>
                <div className={b('certificate-card__badge', { status })}>
                    {status === CERTIFICATE_STATUSES.OPEN ? <CheckCircle /> : <Lock />}
                </div>
                <div className="certificate-card__content">
                    <Text
                        className="certificate-card__title"
                        id={`teacherLK.card.title.${slug}`}
                        typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLRegular}
                    />
                    {status === CERTIFICATE_STATUSES.OPEN &&
                        view === CERTIFICATE_VIEWS.WITH_VERIFICATION &&
                        onSecondaryButtonClick && (
                            <CertificateCardButtonChangeData onChangeDataButtonClick={onSecondaryButtonClick} />
                        )}
                    {status === CERTIFICATE_STATUSES.OPEN ? (
                        <CertificateCardButtonOpen onOpenButtonClick={handleOpenButtonClick} />
                    ) : (
                        <CertificateCardButtonLocked onLockedButtonClick={handleLockedButtonClick} />
                    )}
                </div>
            </div>
            <CertificateCardModal closeModal={() => setModalOpened(false)} opened={modalOpened} slug={slug} />
        </>
    );
};
